import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
  Dot,
} from 'recharts';
import { useMsal } from '@azure/msal-react';
import { config } from '../config/config';
import { saveBlob } from '../utils/saveFile';
import { addReading, computeStats } from '../utils/latencyBuffer';
import type { LatencyReading } from '../utils/latencyBuffer';

const PING_INTERVAL_MS = 1000;
const TIMEOUT_MS = 10000;
const HEALTH_URL = config.API_URL.replace(/\/api\/v1\/?$/, '') + '/health';

function latencyColor(ms: number): string {
  if (ms < 150) return '#22c55e';
  if (ms < 500) return '#eab308';
  return '#ef4444';
}

function formatMs(ms: number | null): string {
  if (ms === null) return '—';
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
  return `${ms}ms`;
}

function StatCard({ label, value, valueClass }: { label: string; value: string; valueClass?: string }) {
  return (
    <div className="bg-panel rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-3 flex flex-col items-center min-w-[90px]">
      <span className={`text-xl font-bold tabular-nums ${valueClass ?? 'text-body'}`}>
        {value}
      </span>
      <span className="text-xs text-muted mt-0.5">{label}</span>
    </div>
  );
}

interface ChartEntry {
  index: number;
  ms: number | null;
  timedOut: boolean;
}

const CustomDot = (props: any) => {
  const { cx, cy, payload } = props;
  if (payload.timedOut || payload.ms === null) return null;
  return <circle cx={cx} cy={cy} r={3} fill={latencyColor(payload.ms)} stroke="none" />;
};

function downloadReport(buffer: LatencyReading[], stats: { current: number | null; avg: number; min: number; max: number; timeouts: number }, userName: string, publicIp: string) {
  const generated = new Date().toISOString().replace('T', ' ').slice(0, 19);
  const lines: string[] = [
    'gwi360 Network Latency Report',
    `Generated : ${generated}`,
    `User      : ${userName}`,
    `Public IP : ${publicIp}`,
    `Endpoint  : ${HEALTH_URL}`,
    `Pings     : ${buffer.length}`,
    `Avg       : ${stats.avg ? `${stats.avg}ms` : '—'}`,
    `Min       : ${stats.min ? `${stats.min}ms` : '—'}`,
    `Max       : ${stats.max ? `${stats.max}ms` : '—'}`,
    `Timeouts  : ${stats.timeouts}`,
    '',
    'Timestamp                    Latency   Status',
    '─'.repeat(52),
  ];

  for (const r of buffer) {
    const ts = new Date(r.ts).toISOString().replace('T', ' ').slice(0, 23);
    const latency = r.timedOut ? 'TIMEOUT' : `${r.ms}ms`;
    const status = r.timedOut ? 'timeout' : r.ms < 150 ? 'good' : r.ms < 500 ? 'slow' : 'bad';
    lines.push(`${ts}    ${latency.padEnd(10)}${status}`);
  }

  const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
  void saveBlob(blob, `latency-report-${generated.replace(/[: ]/g, '-')}.txt`);
}

export default function NetworkMonitor() {
  const [isDark, setIsDark] = useState<boolean>(() => document.documentElement.classList.contains('dark'));
  useEffect(() => {
    const check = () => setIsDark(document.documentElement.classList.contains('dark'));
    check();
    const observer = new MutationObserver(() => check());
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  const { accounts } = useMsal();
  const userName = accounts[0]?.name || accounts[0]?.username || 'Unknown';
  const [buffer, setBuffer] = useState<LatencyReading[]>([]);
  const [paused, setPaused] = useState(false);
  const [publicIp, setPublicIp] = useState('fetching…');
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    fetch('https://api.ipify.org?format=json')
      .then(r => r.json())
      .then(d => setPublicIp(d.ip))
      .catch(() => setPublicIp('unavailable'));
  }, []);

  const ping = useCallback(async () => {
    const controller = new AbortController();
    abortRef.current = controller;
    const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);
    const start = Date.now();
    try {
      await fetch(HEALTH_URL, { signal: controller.signal });
      const ms = Date.now() - start;
      setBuffer(prev => addReading(prev, { ts: start, ms, timedOut: false }));
    } catch {
      setBuffer(prev => addReading(prev, { ts: start, ms: 0, timedOut: true }));
    } finally {
      clearTimeout(timeoutId);
    }
  }, []);

  useEffect(() => {
    if (paused) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }
    ping();
    intervalRef.current = setInterval(ping, PING_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      abortRef.current?.abort();
    };
  }, [paused, ping]);

  const stats = useMemo(() => computeStats(buffer), [buffer]);

  const chartData: ChartEntry[] = buffer.map((r, i) => ({
    index: i,
    ms: r.timedOut ? null : r.ms,
    timedOut: r.timedOut,
  }));

  const currentColor = stats.current === null
    ? 'text-red-500'
    : stats.current < 150
      ? 'text-green-500'
      : stats.current < 500
        ? 'text-yellow-500'
        : 'text-red-500';

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-body">Network Latency Monitor</h1>
          <span className="flex items-center gap-1.5 text-sm text-muted">
            <span className={`inline-block w-2 h-2 rounded-full ${paused ? 'bg-gray-400' : 'bg-green-500 animate-pulse'}`} />
            {paused ? 'Paused' : 'Live'}
          </span>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => downloadReport(buffer, stats, userName, publicIp)}
            disabled={buffer.length === 0}
            className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-label transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Download
          </button>
          <button
            onClick={() => setPaused(p => !p)}
            className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-label transition-colors"
          >
            {paused ? 'Resume' : 'Pause'}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <StatCard
          label="Current"
          value={formatMs(stats.current)}
          valueClass={currentColor}
        />
        <StatCard label="Avg" value={stats.avg ? formatMs(stats.avg) : '—'} />
        <StatCard label="Min" value={stats.min ? formatMs(stats.min) : '—'} />
        <StatCard label="Max" value={stats.max ? formatMs(stats.max) : '—'} />
        <StatCard
          label="Timeouts"
          value={String(stats.timeouts)}
          valueClass={stats.timeouts > 0 ? 'text-red-500' : undefined}
        />
        <StatCard label="Pings" value={String(buffer.length)} />
      </div>

      <div className="bg-panel rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        {buffer.length === 0 ? (
          <div className="flex items-center justify-center h-48 text-gray-400 dark:text-gray-500 text-sm">
            Waiting for first ping…
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} />
              <XAxis dataKey="index" hide />
              <YAxis
                tickFormatter={v => `${v}ms`}
                width={60}
                tick={{ fontSize: 11, fill: isDark ? '#9ca3af' : '#6b7280' }}
                domain={[0, 'auto']}
              />
              <RechartsTooltip
                formatter={(value: any, _name: any, entry: any) => {
                  if (entry.payload.timedOut) return ['Timeout', 'Result'];
                  return [`${value}ms`, 'Latency'];
                }}
                labelFormatter={() => ''}
                contentStyle={{ fontSize: 12 }}
              />
              <ReferenceLine y={150} stroke="#eab308" strokeDasharray="4 4" strokeOpacity={0.6} />
              <ReferenceLine y={500} stroke="#ef4444" strokeDasharray="4 4" strokeOpacity={0.6} />
              <Line
                type="monotone"
                dataKey="ms"
                stroke="#6366f1"
                strokeWidth={1.5}
                dot={<CustomDot />}
                activeDot={{ r: 4 }}
                connectNulls={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
        <div className="flex gap-4 mt-3 text-xs text-muted">
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5 bg-green-500" /> &lt;150ms good</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5 bg-yellow-500" /> 150–500ms slow</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5 bg-red-500" /> &gt;500ms bad</span>
        </div>
      </div>

      <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
        Pinging <code>{HEALTH_URL}</code> every {PING_INTERVAL_MS / 1000}s · last {buffer.length} of 150 readings shown
      </p>
    </div>
  );
}
